import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';

export type LearningFilterValues = { source: string; period: string };

export const learningPeriodLabels: Record<string, string> = {
  '7': 'Últimos 7 dias',
  '30': 'Últimos 30 dias',
  '90': 'Últimos 90 dias',
  all: 'Todo o período',
};

interface LearningFilterControlProps {
  value: LearningFilterValues;
  onApply: (next: LearningFilterValues) => void;
  includeChatCorrection: boolean;
}

const periodOptions = Object.entries(learningPeriodLabels);
const isChatSource = (source: string) => source === 'chat_correction' || source === 'chat_instruction';

export const LearningFilterControl: React.FC<LearningFilterControlProps> = ({ value, onApply, includeChatCorrection }) => {
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<LearningFilterValues>(value);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const sourceId = useId();
  const panelId = useId();
  const count = Number(Boolean(value.source)) + Number(value.period !== 'all');

  const closePanel = () => setOpen(false);
  const openPanel = () => {
    setDraft({ source: !includeChatCorrection && isChatSource(value.source) ? '' : value.source, period: value.period });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 360;
      const height = panelRef.current?.getBoundingClientRect().height || 380;
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      const below = rect.bottom + 12;
      const top = below + height <= window.innerHeight - 8
        ? below
        : rect.top - height - 12 >= 8
          ? rect.top - height - 12
          : Math.max(8, window.innerHeight - height - 8);
      setPosition({ top, left });
    };
    place();
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closePanel();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), select:not([disabled])') || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!panelRef.current?.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panelRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('keydown', onKeyDown);
      trigger?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (open && !includeChatCorrection && isChatSource(draft.source)) {
      setDraft(current => ({ ...current, source: '' }));
    }
  }, [open, includeChatCorrection, draft.source]);

  const triggerColors = count
    ? isDark
      ? 'border-sminy-dark-focus bg-sminy-dark-selection text-sminy-dark-focus'
      : 'border-sminy-primary bg-sminy-blue-light text-sminy-primary'
    : isDark
      ? 'border-sminy-dark-border bg-sminy-dark-surface text-sminy-dark-text-primary'
      : 'border-sminy-border bg-white text-sminy-text-primary';
  const surfaceColors = isDark
    ? 'border-sminy-dark-border bg-sminy-dark-elevated text-sminy-dark-text-primary'
    : 'border-sminy-border bg-white text-sminy-text-primary';
  const inputColors = isDark
    ? 'border-sminy-dark-border bg-sminy-dark-surface text-sminy-dark-text-primary'
    : 'border-sminy-border bg-white text-sminy-text-primary';
  const mutedColors = isDark ? 'text-sminy-dark-muted' : 'text-sminy-muted';

  return <>
    <button
      ref={triggerRef}
      type="button"
      aria-label={count ? `Filtros, ${count} ativos` : 'Filtros'}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-controls={open ? panelId : undefined}
      onClick={openPanel}
      className={`inline-flex h-11 w-28 shrink-0 items-center justify-center gap-1.5 rounded-xl border text-xs font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary sm:h-10 sm:w-[132px] ${triggerColors}`}
    >
      <SlidersHorizontal aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span>Filtros</span>
      {count > 0 && <span aria-hidden="true" className="grid h-5 min-w-5 place-items-center rounded-full bg-sminy-primary px-1 text-[11px] font-medium text-white">{count}</span>}
    </button>
    {open && createPortal(
      <div className="fixed inset-0 z-[9998]">
        <div aria-hidden="true" data-testid="learning-filter-backdrop" className="absolute inset-0 bg-sminy-navy/50 sm:bg-transparent" onClick={closePanel} />
        <div
          id={panelId}
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          style={{ '--learning-filter-top': `${position.top}px`, '--learning-filter-left': `${position.left}px` } as React.CSSProperties}
          className={`fixed bottom-0 left-0 right-0 flex max-h-[85dvh] min-h-[min(446px,85dvh)] flex-col overflow-y-auto rounded-t-[20px] border shadow-xl outline-none pb-[env(safe-area-inset-bottom)] sm:bottom-auto sm:right-auto sm:top-[var(--learning-filter-top)] sm:left-[var(--learning-filter-left)] sm:max-h-[calc(100dvh-16px)] sm:min-h-0 sm:w-[360px] sm:rounded-2xl sm:pb-0 ${surfaceColors}`}
        >
          <div className="relative mx-5 mt-5 h-6">
            <h2 id={titleId} className="text-base font-medium leading-6">Filtros</h2>
            <button ref={closeRef} type="button" aria-label="Fechar filtros" onClick={closePanel} className={`absolute right-[-10px] top-[-10px] grid h-11 w-11 place-items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary sm:right-[-2px] sm:top-0 sm:h-6 sm:w-6 ${mutedColors}`}>
              <X aria-hidden="true" className="h-5 w-5" />
            </button>
          </div>
          <div className="px-5">
            <div className="mt-4">
              <label htmlFor={sourceId} className="mb-2 block text-xs font-medium">Origem</label>
              <div className="relative">
                <select
                  id={sourceId}
                  value={draft.source}
                  onChange={event => setDraft(current => ({ ...current, source: event.target.value }))}
                  className={`h-11 w-full appearance-none rounded-xl border px-3 pr-10 text-xs outline-none focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 sm:h-10 ${inputColors}`}
                >
                  <option value="">Todas as origens</option>
                  <option value="history">Histórico do WhatsApp</option>
                  <option value="continuous">Atendimento da equipe</option>
                  {includeChatCorrection && <option value="chat_correction">Correção no chat</option>}
                  {includeChatCorrection && <option value="chat_instruction">Instrução no chat</option>}
                </select>
                <ChevronDown aria-hidden="true" className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 ${mutedColors}`} />
              </div>
            </div>
            <fieldset className="mt-6">
              <legend className="text-xs font-medium">Período</legend>
              <p className={`mb-3 mt-1 text-[11px] ${mutedColors}`}>Data de criação do aprendizado</p>
              <div className="grid grid-cols-2 gap-2">
                {periodOptions.map(([period, label]) => {
                  const selected = draft.period === period;
                  const colors = selected
                    ? isDark
                      ? 'border-sminy-dark-focus bg-sminy-dark-selection text-sminy-dark-focus'
                      : 'border-sminy-primary bg-sminy-blue-light text-sminy-primary'
                    : inputColors;
                  return <button key={period} type="button" aria-pressed={selected} onClick={() => setDraft(current => ({ ...current, period }))} className={`min-h-11 rounded-xl border px-1 text-center text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary sm:min-h-9 ${colors}`}>{label}</button>;
                })}
              </div>
            </fieldset>
          </div>
          <div className="px-5 pb-5 pt-5">
            <div className={`mb-5 border-t ${isDark ? 'border-sminy-dark-border' : 'border-sminy-border'}`} />
            <div className="flex gap-3">
              <button type="button" onClick={() => setDraft({ source: '', period: 'all' })} className={`h-11 w-[110px] shrink-0 rounded-xl border px-2 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary sm:h-10 sm:w-[116px] ${inputColors}`}>Limpar filtros</button>
              <button type="button" onClick={() => { onApply({ source: !includeChatCorrection && isChatSource(draft.source) ? '' : draft.source, period: draft.period }); closePanel(); }} className="h-11 min-w-0 flex-1 rounded-xl bg-sminy-primary px-2 text-xs font-medium text-white hover:bg-sminy-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 sm:h-10">Aplicar filtros</button>
            </div>
          </div>
        </div>
      </div>, document.body)}
  </>;
};

export default LearningFilterControl;
